import type { RecurringCharge } from "@/lib/types/recurring-charges";
import type { DetailAction } from "@/components/shared/UniversalDetailPage/types/detailPage";
import { universalAskMeridianAction } from "@/lib/actions/definitions/universal-actions";

/**
 * Detail page actions for recurring charges
 */
export const recurringChargeDetailActions: DetailAction<RecurringCharge>[] = [
  // AI ASSISTANT ACTIONS — focused Meridian chat about this recurring charge
  universalAskMeridianAction<RecurringCharge>(),

  // ========== PRIMARY ACTIONS (Header) ==========
  // Charge card on file action - Admin only
  {
    id: "charge-card-on-file",
    label: "Charge Card",
    icon: "zap",
    variant: "default",
    primary: true,
    action: "chargeCardOnFile",
    allowedRoles: ["wtc_admin"],
    tooltip: "Charge the card on file for this recurring charge",
  },

  // Change schedule action - Admin only
  {
    id: "change-schedule",
    label: "Change Schedule",
    icon: "calendar",
    variant: "outline",
    primary: true,
    action: "changeSchedule",
    allowedRoles: ["wtc_admin"],
    tooltip: "Change the charge schedule (daily, weekly, monthly)",
  },

  // ========== SECONDARY ACTIONS (Menu) ==========
  // View details action - Admin only
  {
    id: "view-details",
    label: "View Details",
    icon: "eye",
    variant: "outline",
    primary: false,
    action: "viewDetails",
    allowedRoles: ["wtc_admin"],
  },

  // Delete action - Admin only
  {
    id: "delete-recurring-charge",
    label: "Delete",
    icon: "trash2",
    variant: "destructive",
    primary: false,
    action: "deleteRecurringCharge",
    allowedRoles: ["wtc_admin"],
    tooltip: "Delete this recurring charge",
  },
];

/**
 * Detail page configuration for recurring charges
 * This is used to define the layout and actions for the detail view
 */
export const RECURRING_CHARGE_DETAIL_CONFIG = {
  entity: "recurring-charges",
  displayName: "Recurring Charge",

  tabs: [
    {
      id: "details",
      label: "Details",
      content: {
        type: "fields" as const,
        sections: [
          {
            id: "charge-info",
            title: "Charge Information",
            fields: ["customer_id", "schedule_type", "next_charge_date", "last_charged_at"],
            layout: "grid" as const,
            gridColumns: 2,
          },
          {
            id: "charge-details",
            title: "Charge Details",
            fields: ["metadata"],
            layout: "full" as const,
          },
          {
            id: "system-info",
            title: "System Information",
            fields: ["realm_id", "admin_email", "created_at", "updated_at"],
            layout: "grid" as const,
            gridColumns: 2,
            collapsible: true,
            defaultCollapsed: true,
          },
        ],
      },
    },
  ],

  fields: {
    id: {
      type: "text",
      label: "ID",
      editable: false,
    },
    customer_id: {
      type: "text",
      label: "Customer ID",
      editable: false,
    },
    schedule_type: {
      type: "select",
      label: "Schedule Type",
      editable: false,
      options: [
        { label: "Daily", value: "daily" },
        { label: "Weekly", value: "weekly" },
        { label: "Monthly", value: "monthly" },
      ],
    },
    last_charged_at: {
      type: "date",
      label: "Last Charged",
      editable: false,
    },
    next_charge_date: {
      type: "date",
      label: "Next Charge Date",
      editable: false,
    },
    metadata: {
      type: "json",
      label: "Charge Details",
      editable: true,
    },
    realm_id: {
      type: "text",
      label: "Realm ID",
      editable: false,
    },
    admin_email: {
      type: "text",
      label: "Admin Email",
      editable: false,
    },
    created_at: {
      type: "date",
      label: "Created",
      editable: false,
    },
    updated_at: {
      type: "date",
      label: "Updated",
      editable: false,
    },
  },

  actions: recurringChargeDetailActions,
};

/**
 * Enhanced detail config with all actions
 */
export const ENHANCED_RECURRING_CHARGE_DETAIL_CONFIG = RECURRING_CHARGE_DETAIL_CONFIG;
